import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MainLayout from "../components/layout/MainLayout";
import UserList from "../features/users/UserList";
import UserForm from "../features/users/UserForm";
import UserDetails from "../features/users/userDetails";
import UserStatusForm from "../features/users/UserStatusForm";
import { useNotification } from "../context/NotificationContext";
import userService from "../services/userService";

const Users = () => {
  const { t } = useTranslation(["users", "common"]);
  const [showForm, setShowForm] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess, showError } = useNotification();

  const handleCreate = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEdit = async (user) => {
    try {
      // Load full user details for editing
      setLoading(true);
      const response = await userService.getUserDetails(user.id);
      if (response) {
        setEditingUser(response);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error loading user details:", error);
      showError(t("users:messages.loadDetailFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (user) => {
    try {
      setLoading(true);
      const response = await userService.getUserDetails(user.id);
      if (response) {
        setViewingUser(response);
      }
    } catch (error) {
      console.error("Error loading user details:", error);
      showError(t("users:messages.loadDetailFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseView = () => {
    setViewingUser(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingUser) {
        response = await userService.updateUser(editingUser.id, formData);
        if (response) {
          showSuccess(t("users:messages.updateSuccess"));
        }
      } else {
        response = await userService.createUser(formData);
        if (response) {
          showSuccess(t("users:messages.createSuccess"));
        }
      }

      setShowForm(false);
      setEditingUser(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleStatusEdit = async (user) => {
    setEditingUser(user);
    setShowStatusForm(true);
  };

  const handleStatusFormSubmit = async (formData) => {
    try {
      setLoading(true);
      await userService.changeUserStatusAndRole(editingUser.id, formData);

      setShowStatusForm(false);
      setRefreshTrigger((prev) => prev + 1);
      showSuccess(t("users:messages.statusUpdateSuccess"));
    } catch (error) {
      console.error("Error updating user status:", error);
      showError(t("users:messages.statusUpdateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFormCancel = () => {
    setShowStatusForm(false);
    setEditingUser(null);
  };

  const handleDelete = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await userService.deleteUser(deletingUser.id);

      setShowDeleteModal(false);
      setDeletingUser(null);
      setRefreshTrigger((prev) => prev + 1);
      showSuccess(t("users:messages.deleteAccountSuccess"));
    } catch (error) {
      console.error("Error deleting user:", error);
      showError(t("users:messages.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingUser(null);
  };

  return (
    <MainLayout>
      <div className="users-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("users:management")}</h1>
            <p className="text-muted mb-0">
              {t("users:managementSubtitle")}
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            {t("users:addUser")}
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${
                        editingUser ? "fa-edit" : "fa-plus"
                      } me-2`}
                    ></i>
                    {editingUser ? t("users:editUser") : t("users:addNewUser")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <UserForm
                    user={editingUser}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewingUser && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-user me-2"></i>
                    {t("users:userDetails")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseView}
                  ></button>
                </div>
                <div className="modal-body">
                  <UserDetails
                    user={viewingUser}
                    onClose={handleCloseView}
                    onEdit={handleEdit}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      handleCloseView();
                      handleEdit(viewingUser);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    {t("users:editUser")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseView}
                  >
                    {t("common:buttons.close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Form Modal */}
        {showStatusForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-user-shield me-2"></i>
                    {t("users:manageStatusRole")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleStatusFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <UserStatusForm
                    user={editingUser}
                    onSubmit={handleStatusFormSubmit}
                    onCancel={handleStatusFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {t("users:confirmDeletion")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleDeleteCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>{t("common:messages.warning") || "Warning"}:</strong> {t("users:deleteWarning")}
                  </div>
                  <p>
                    {t("users:deleteConfirmText", { name: deletingUser?.fullName, id: deletingUser?.id })}
                  </p>
                  <p className="text-muted mb-0">
                    {t("users:deleteIrreversible")}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDeleteCancel}
                    disabled={loading}
                  >
                    {t("common:buttons.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteConfirm}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("common:actions.deleting")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash me-2"></i>
                        {t("common:buttons.delete")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users List */}
        <UserList
          onEdit={handleEdit}
          onView={handleView}
          onStatusEdit={handleStatusEdit}
          onDelete={handleDelete}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Users;
